"use client";
import type { Zone, ZoneSummary } from "@gettactic/api";
import { HookFormColorPickerInput } from "@gettactic/components/src/form/ColorPickerInput";
import { HookFormIconPickerInput } from "@gettactic/components/src/form/IconPickerInput";
import { HookFormTextareaInput } from "@gettactic/components/src/form/TextareaInput";
import { HookFormTextInput } from "@gettactic/components/src/form/TextInput";
import { HookFormUnifiedSelectInput } from "@gettactic/components/src/form/UnifiedSelectInput/HookFormUnifiedSelectInput";
import type { IconValue } from "@gettactic/components/src/icons";
import { CrudModal } from "@gettactic/components/src/overlay/modals/CrudModal";
import { logError } from "@gettactic/helpers";
import { useInvalidateZones, useZonesQuery } from "@gettactic/hooks";
import React from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { api } from "@/lib/api/api";
import { parseZoneIcon, serializeZoneIcon, zoneSubtreeIds } from "./zoneTree";

const ROOT_PARENT = "__root__";
const DEFAULT_COLOR = "#3B82F6";

export interface CreateOrEditZoneProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /** Zone being edited; omit to create. */
  zone?: Zone | ZoneSummary;
  /** Preselected parent when creating a sub zone. */
  parentId?: string | null;
}

interface ZoneFormData {
  name: string;
  color: string;
  icon?: IconValue;
  description: string;
  parent_id: string;
}

export const CreateOrEditZone: React.FC<CreateOrEditZoneProps> = ({
  isOpen,
  onOpenChange,
  zone,
  parentId
}) => {
  const isEditing = !!zone?.id;
  const { data: zones } = useZonesQuery(undefined, isOpen);
  const invalidateZones = useInvalidateZones();

  const defaultValues: ZoneFormData = React.useMemo(
    () => ({
      name: zone?.name ?? "",
      color: zone?.color ?? DEFAULT_COLOR,
      icon: parseZoneIcon(zone?.icon),
      description: zone?.description ?? "",
      parent_id: zone?.parent_id ?? parentId ?? ROOT_PARENT
    }),
    [zone, parentId]
  );

  const form = useForm<ZoneFormData>({ defaultValues });

  React.useEffect(() => {
    if (isOpen) form.reset(defaultValues);
  }, [isOpen, defaultValues, form]);

  // A zone cannot be moved under itself or one of its descendants.
  const parentItems = React.useMemo(() => {
    const excluded = zone
      ? zoneSubtreeIds(zone.id, zones ?? [])
      : new Set<string>();
    return [
      { id: ROOT_PARENT, name: "(none — top-level zone)" },
      ...(zones ?? [])
        .filter((candidate) => !excluded.has(candidate.id))
        .map((candidate) => ({ id: candidate.id, name: candidate.name }))
    ];
  }, [zones, zone]);

  const color = form.watch("color");

  const onSubmit = async () => {
    const values = form.getValues();
    const payload = {
      name: values.name.trim(),
      color: values.color || DEFAULT_COLOR,
      icon: serializeZoneIcon(values.icon),
      description: values.description.trim() || null,
      parent_id: values.parent_id === ROOT_PARENT ? null : values.parent_id
    };
    try {
      if (isEditing && zone) {
        await api.client.zones.update(zone.id, payload);
      } else {
        await api.client.zones.create(payload);
      }
      await invalidateZones();
      toast.success(isEditing ? "Zone saved" : "Zone created");
      onOpenChange(false);
    } catch (error) {
      logError("Error saving zone:", error);
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : "The zone could not be saved"
      );
      throw error;
    }
  };

  return (
    <CrudModal
      title={isEditing ? "Edit Zone" : "Add Zone"}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="lg"
      isEditing={isEditing}
      form={form}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      submitLabel={isEditing ? "Save Changes" : "Add Zone"}
    >
      <div className="flex flex-col gap-6">
        <div className="flex items-end gap-4">
          <div className="w-14 shrink-0">
            <HookFormIconPickerInput
              label="Icon"
              form={form}
              name="icon"
              tintColor={color}
            />
          </div>
          <div className="min-w-0 flex-1">
            <HookFormTextInput
              form={form}
              name="name"
              label="Name"
              placeholder="Engineering, Quiet zone, Sales…"
              isRequired
            />
          </div>
        </div>
        <HookFormColorPickerInput
          form={form}
          name="color"
          label="Color"
          isRequired
        />
        <HookFormUnifiedSelectInput
          form={form}
          name="parent_id"
          label="Parent zone"
          description="Nest this zone under another to roll its desks and area up into it."
          items={parentItems}
          searchable
        />
        <HookFormTextareaInput
          form={form}
          name="description"
          label="Description"
          placeholder="What this zone is for (optional)"
        />
      </div>
    </CrudModal>
  );
};
